import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/cn";
import type { DirectorTeamRow } from "@/api/types";

/**
 * S5 Block 2 — Team comparison (per supervisor, team-grain). Click-row → team
 * drilldown (read-only). NOT a copy of the Supervisor manager-table (that is
 * per-manager within one team; this is per-team across the org). Revenue =
 * rolling-30d-actual; AR-risk null = BI data-unavailable.
 */
function money(v: number | null): string {
  if (v === null) return "—";
  if (Math.abs(v) >= 1_000_000) return `₴ ${(v / 1_000_000).toFixed(2)}М`;
  if (Math.abs(v) >= 1_000) return `₴ ${Math.round(v / 1_000)}К`;
  return `₴ ${Math.round(v)}`;
}

export function DirectorTeamComparison({ rows }: { rows: DirectorTeamRow[] }) {
  const navigate = useNavigate();
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
        Немає команд для порівняння.
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="px-4 py-2.5 text-left font-semibold">Команда / Супервайзер</th>
            <th className="px-4 py-2.5 text-right font-semibold">Дохід 30д</th>
            <th className="px-4 py-2.5 text-right font-semibold">Без next-action</th>
            <th className="px-4 py-2.5 text-right font-semibold">Прострочені</th>
            <th className="px-4 py-2.5 text-right font-semibold">Зустр. без звіту</th>
            <th className="px-4 py-2.5 text-right font-semibold">AR-risk</th>
            <th className="px-4 py-2.5 text-right font-semibold">Крит. реклам.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.teamId}
              onClick={() => navigate(`/clients?scope=team&teamId=${r.teamId}`)}
              className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/40"
            >
              <td className="px-4 py-3 text-left text-[13.5px]">
                <div className="font-medium">{r.teamName}</div>
                {r.supervisorName && <div className="text-xs text-muted-foreground">{r.supervisorName}</div>}
              </td>
              <td className="px-4 py-3 text-right tabular-nums">{money(r.revenue30d)}</td>
              <Num v={r.clientsNoAction} warnAt={20} />
              <Num v={r.overdueTasks} badAt={5} warnAt={1} />
              <Num v={r.meetingsNoReport} warnAt={3} />
              <td className={cn("px-4 py-3 text-right tabular-nums", (r.arRiskClients ?? 0) >= 5 && "font-semibold text-[hsl(var(--destructive))]")}>
                {r.arRiskClients === null ? "—" : r.arRiskClients}
              </td>
              <Num v={r.criticalComplaints} badAt={2} warnAt={1} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Num({ v, badAt, warnAt }: { v: number; badAt?: number; warnAt?: number }) {
  const tone = badAt && v >= badAt ? "bad" : warnAt && v >= warnAt ? "warn" : "none";
  return (
    <td
      className={cn(
        "px-4 py-3 text-right tabular-nums",
        tone === "bad" && "font-semibold text-[hsl(var(--destructive))]",
        tone === "warn" && "font-medium text-[hsl(var(--warning-foreground))]",
      )}
    >
      {v}
    </td>
  );
}
